import React, { useCallback, useEffect, useMemo, useState } from "react";
import { calls as callsApi } from "../api/endpoints";
import type { CallRate, CallType, ServiceLevel } from "../types";

type SetupTab = "service-levels" | "rates";

const emptyServiceLevelForm = {
  name: "",
  description: "",
  abbreviation: "",
  caller_reimburse: 0,
  hmc_fee: 0,
  fee: 0,
  is_active: true,
};

const emptyRateForm = {
  call_type_id: 0,
  service_level_id: 0,
  rate: 0,
  is_active: true,
};

const money = (value: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(Number(value || 0));

const serviceLevelLabel = (serviceLevel: Pick<ServiceLevel, "name" | "description">) =>
  [serviceLevel.name, serviceLevel.description].filter(Boolean).join(" - ");

export default function CallSetupPage() {
  const [tab, setTab] = useState<SetupTab>("service-levels");
  const [serviceLevels, setServiceLevels] = useState<ServiceLevel[]>([]);
  const [callTypes, setCallTypes] = useState<CallType[]>([]);
  const [rates, setRates] = useState<CallRate[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [editingServiceLevel, setEditingServiceLevel] = useState<ServiceLevel | null>(null);
  const [showServiceLevelForm, setShowServiceLevelForm] = useState(false);
  const [serviceLevelForm, setServiceLevelForm] = useState(emptyServiceLevelForm);
  const [editingRate, setEditingRate] = useState<CallRate | null>(null);
  const [showRateForm, setShowRateForm] = useState(false);
  const [rateForm, setRateForm] = useState(emptyRateForm);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [levels, types, callRates] = await Promise.all([
        callsApi.serviceLevels(true),
        callsApi.types(),
        callsApi.rates(true),
      ]);
      setServiceLevels(levels);
      setCallTypes(types);
      setRates(callRates);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : "Unable to load call setup.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filteredServiceLevels = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return serviceLevels;
    return serviceLevels.filter((level) =>
      [level.name, level.description, level.abbreviation].some((value) =>
        value.toLowerCase().includes(query)
      )
    );
  }, [search, serviceLevels]);

  const filteredRates = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return rates;
    return rates.filter((rate) =>
      [rate.call_type_name, rate.service_level_name, rate.service_level_description].some((value) =>
        value.toLowerCase().includes(query)
      )
    );
  }, [rates, search]);

  const openNewServiceLevel = () => {
    setEditingServiceLevel(null);
    setServiceLevelForm(emptyServiceLevelForm);
    setShowServiceLevelForm(true);
    setError("");
  };

  const openEditServiceLevel = (serviceLevel: ServiceLevel) => {
    setEditingServiceLevel(serviceLevel);
    setServiceLevelForm({
      name: serviceLevel.name,
      description: serviceLevel.description,
      abbreviation: serviceLevel.abbreviation,
      caller_reimburse: Number(serviceLevel.caller_reimburse || 0),
      hmc_fee: Number(serviceLevel.hmc_fee || 0),
      fee: Number(serviceLevel.fee || 0),
      is_active: serviceLevel.is_active,
    });
    setShowServiceLevelForm(true);
    setError("");
  };

  const saveServiceLevel = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      if (editingServiceLevel) {
        await callsApi.updateServiceLevel(editingServiceLevel.id, serviceLevelForm);
        setNotice("Service level updated.");
      } else {
        await callsApi.createServiceLevel(serviceLevelForm);
        setNotice("Service level created. Add its call rate before scheduling shops.");
      }
      setShowServiceLevelForm(false);
      await loadData();
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : "Unable to save service level.");
    } finally {
      setSaving(false);
    }
  };

  const setServiceLevelActive = async (serviceLevel: ServiceLevel, isActive: boolean) => {
    const action = isActive ? "reactivate" : "deactivate";
    if (!window.confirm(`${action[0].toUpperCase()}${action.slice(1)} ${serviceLevelLabel(serviceLevel)}?`)) return;
    setError("");
    try {
      if (isActive) {
        await callsApi.updateServiceLevel(serviceLevel.id, { is_active: true });
      } else {
        await callsApi.deactivateServiceLevel(serviceLevel.id);
      }
      setNotice(`Service level ${isActive ? "reactivated" : "deactivated"}.`);
      await loadData();
    } catch (updateError) {
      setError(updateError instanceof Error ? updateError.message : "Unable to update service level.");
    }
  };

  const openNewRate = () => {
    const firstActiveLevel = serviceLevels.find((level) => level.is_active);
    setEditingRate(null);
    setRateForm({
      ...emptyRateForm,
      call_type_id: callTypes[0]?.id || 0,
      service_level_id: firstActiveLevel?.id || 0,
    });
    setShowRateForm(true);
    setError("");
  };

  const openEditRate = (rate: CallRate) => {
    setEditingRate(rate);
    setRateForm({
      call_type_id: rate.call_type_id,
      service_level_id: rate.service_level_id,
      rate: Number(rate.rate || 0),
      is_active: rate.is_active,
    });
    setShowRateForm(true);
    setError("");
  };

  const saveRate = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      if (editingRate) {
        await callsApi.updateRate(editingRate.id, rateForm);
        setNotice("Call rate updated.");
      } else {
        await callsApi.createRate(rateForm);
        setNotice("Call rate created.");
      }
      setShowRateForm(false);
      await loadData();
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : "Unable to save call rate.");
    } finally {
      setSaving(false);
    }
  };

  const setRateActive = async (rate: CallRate, isActive: boolean) => {
    const action = isActive ? "reactivate" : "deactivate";
    if (!window.confirm(`${action[0].toUpperCase()}${action.slice(1)} this call rate?`)) return;
    setError("");
    try {
      if (isActive) {
        await callsApi.updateRate(rate.id, { is_active: true });
      } else {
        await callsApi.deactivateRate(rate.id);
      }
      setNotice(`Call rate ${isActive ? "reactivated" : "deactivated"}.`);
      await loadData();
    } catch (updateError) {
      setError(updateError instanceof Error ? updateError.message : "Unable to update call rate.");
    }
  };

  const visibleCount = tab === "service-levels" ? filteredServiceLevels.length : filteredRates.length;

  return (
    <div className="space-y-5">
      <header className="page-header">
        <div className="page-header-copy">
          <div className="eyebrow">Scheduling Configuration</div>
          <h1 className="page-title">Call Setup</h1>
          <p className="page-copy">
            Manage the service-level options and pricing used by manual scheduling, batch scheduling, and Excel imports.
          </p>
        </div>
        <div className="page-header-actions">
          <button
            type="button"
            className="button-primary"
            onClick={tab === "service-levels" ? openNewServiceLevel : openNewRate}
          >
            {tab === "service-levels" ? "Add Service Level" : "Add Call Rate"}
          </button>
        </div>
      </header>

      {notice && (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          {notice}
        </div>
      )}
      {error && (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
          {error}
        </div>
      )}

      <section className="surface-panel overflow-hidden">
        <div className="flex flex-col gap-4 border-b border-slate-200 p-4 md:flex-row md:items-center md:justify-between md:px-6">
          <div className="inline-flex w-fit rounded-md border border-slate-200 bg-slate-50 p-1">
            <button
              type="button"
              onClick={() => setTab("service-levels")}
              className={`rounded px-4 py-2 text-sm font-semibold ${tab === "service-levels" ? "bg-white text-slate-950 shadow-sm" : "text-slate-600"}`}
            >
              Service Levels
            </button>
            <button
              type="button"
              onClick={() => setTab("rates")}
              className={`rounded px-4 py-2 text-sm font-semibold ${tab === "rates" ? "bg-white text-slate-950 shadow-sm" : "text-slate-600"}`}
            >
              Call Rates
            </button>
          </div>
          <div className="flex w-full items-center gap-3 md:w-auto">
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className="input-shell md:w-80"
              placeholder={tab === "service-levels" ? "Search service levels" : "Search call rates"}
            />
            <span className="badge-pill tone-neutral whitespace-nowrap">{visibleCount} shown</span>
          </div>
        </div>

        {loading ? (
          <div className="p-10 text-center text-sm text-slate-500">Loading call setup...</div>
        ) : tab === "service-levels" ? (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px] text-sm">
              <thead className="bg-slate-50 text-left">
                <tr>
                  <th className="px-5 py-3">Service Level</th>
                  <th className="px-5 py-3">Code</th>
                  <th className="px-5 py-3 text-right">Shopper Fee</th>
                  <th className="px-5 py-3 text-right">HMC Fee</th>
                  <th className="px-5 py-3 text-right">Base Fee</th>
                  <th className="px-5 py-3">Status</th>
                  <th className="px-5 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredServiceLevels.map((level) => (
                  <tr key={level.id} className="border-t border-slate-100">
                    <td className="px-5 py-4">
                      <div className="font-semibold text-slate-900">{level.name}</div>
                      <div className="mt-1 text-xs text-slate-500">{level.description || "General"}</div>
                    </td>
                    <td className="px-5 py-4 font-mono text-xs text-slate-600">{level.abbreviation || "-"}</td>
                    <td className="px-5 py-4 text-right tabular-nums">{money(level.caller_reimburse)}</td>
                    <td className="px-5 py-4 text-right tabular-nums">{money(level.hmc_fee)}</td>
                    <td className="px-5 py-4 text-right tabular-nums">{money(level.fee)}</td>
                    <td className="px-5 py-4">
                      <span className={`badge-pill ${level.is_active ? "tone-success" : "tone-neutral"}`}>
                        {level.is_active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex justify-end gap-2">
                        <button type="button" onClick={() => openEditServiceLevel(level)} className="button-ghost !px-3 !py-2 text-xs">
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => setServiceLevelActive(level, !level.is_active)}
                          className={level.is_active ? "button-danger !px-3 !py-2 text-xs" : "button-secondary !px-3 !py-2 text-xs"}
                        >
                          {level.is_active ? "Deactivate" : "Activate"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredServiceLevels.length === 0 && <div className="p-10 text-center text-sm text-slate-500">No service levels match this search.</div>}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] text-sm">
              <thead className="bg-slate-50 text-left">
                <tr>
                  <th className="px-5 py-3">Call Type</th>
                  <th className="px-5 py-3">Service Level</th>
                  <th className="px-5 py-3 text-right">Call Cost</th>
                  <th className="px-5 py-3">Status</th>
                  <th className="px-5 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRates.map((rate) => (
                  <tr key={rate.id} className="border-t border-slate-100">
                    <td className="px-5 py-4 font-semibold text-slate-900">{rate.call_type_name}</td>
                    <td className="px-5 py-4">
                      <div className="font-medium text-slate-900">{rate.service_level_name}</div>
                      <div className="mt-1 text-xs text-slate-500">{rate.service_level_description || "General"}</div>
                    </td>
                    <td className="px-5 py-4 text-right font-semibold tabular-nums">{money(rate.rate)}</td>
                    <td className="px-5 py-4">
                      <span className={`badge-pill ${rate.is_active ? "tone-success" : "tone-neutral"}`}>
                        {rate.is_active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex justify-end gap-2">
                        <button type="button" onClick={() => openEditRate(rate)} className="button-ghost !px-3 !py-2 text-xs">Edit</button>
                        <button
                          type="button"
                          onClick={() => setRateActive(rate, !rate.is_active)}
                          className={rate.is_active ? "button-danger !px-3 !py-2 text-xs" : "button-secondary !px-3 !py-2 text-xs"}
                        >
                          {rate.is_active ? "Deactivate" : "Activate"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredRates.length === 0 && <div className="p-10 text-center text-sm text-slate-500">No call rates match this search.</div>}
          </div>
        )}
      </section>

      {showServiceLevelForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 p-4">
          <div className="surface-panel w-full max-w-2xl overflow-hidden">
            <div className="border-b border-slate-200 px-6 py-5">
              <div className="eyebrow">Scheduling Option</div>
              <h2 className="mt-1 text-xl font-semibold">{editingServiceLevel ? "Edit Service Level" : "Add Service Level"}</h2>
            </div>
            <form onSubmit={saveServiceLevel} className="space-y-5 p-6">
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="label-caps mb-1.5 block">Name</label>
                  <input
                    value={serviceLevelForm.name}
                    onChange={(event) => setServiceLevelForm({ ...serviceLevelForm, name: event.target.value })}
                    className="input-shell"
                    placeholder="Proposal"
                    required
                  />
                </div>
                <div>
                  <label className="label-caps mb-1.5 block">Client / Program</label>
                  <input
                    value={serviceLevelForm.description}
                    onChange={(event) => setServiceLevelForm({ ...serviceLevelForm, description: event.target.value })}
                    className="input-shell"
                    placeholder="Dreamscape"
                  />
                </div>
                <div>
                  <label className="label-caps mb-1.5 block">Abbreviation</label>
                  <input
                    value={serviceLevelForm.abbreviation}
                    onChange={(event) => setServiceLevelForm({ ...serviceLevelForm, abbreviation: event.target.value })}
                    className="input-shell uppercase"
                    placeholder="DSP"
                    maxLength={10}
                  />
                </div>
                <label className="flex min-h-[46px] items-center gap-3 self-end rounded-md border border-slate-200 px-4 py-3 text-sm font-medium text-slate-700">
                  <input
                    type="checkbox"
                    checked={serviceLevelForm.is_active}
                    onChange={(event) => setServiceLevelForm({ ...serviceLevelForm, is_active: event.target.checked })}
                  />
                  Active in scheduling lists
                </label>
              </div>
              <div className="grid gap-4 border-t border-slate-200 pt-5 sm:grid-cols-3">
                <div>
                  <label className="label-caps mb-1.5 block">Shopper Fee</label>
                  <input type="number" min="0" step="0.01" value={serviceLevelForm.caller_reimburse} onChange={(event) => setServiceLevelForm({ ...serviceLevelForm, caller_reimburse: Number(event.target.value) })} className="input-shell" />
                </div>
                <div>
                  <label className="label-caps mb-1.5 block">HMC Fee</label>
                  <input type="number" min="0" step="0.01" value={serviceLevelForm.hmc_fee} onChange={(event) => setServiceLevelForm({ ...serviceLevelForm, hmc_fee: Number(event.target.value) })} className="input-shell" />
                </div>
                <div>
                  <label className="label-caps mb-1.5 block">Base Fee</label>
                  <input type="number" min="0" step="0.01" value={serviceLevelForm.fee} onChange={(event) => setServiceLevelForm({ ...serviceLevelForm, fee: Number(event.target.value) })} className="input-shell" />
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setShowServiceLevelForm(false)} className="button-ghost" disabled={saving}>Cancel</button>
                <button type="submit" className="button-primary" disabled={saving}>{saving ? "Saving..." : "Save Service Level"}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showRateForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 p-4">
          <div className="surface-panel w-full max-w-xl overflow-hidden">
            <div className="border-b border-slate-200 px-6 py-5">
              <div className="eyebrow">Pricing</div>
              <h2 className="mt-1 text-xl font-semibold">{editingRate ? "Edit Call Rate" : "Add Call Rate"}</h2>
            </div>
            <form onSubmit={saveRate} className="space-y-5 p-6">
              <div>
                <label className="label-caps mb-1.5 block">Call Type</label>
                <select value={rateForm.call_type_id} onChange={(event) => setRateForm({ ...rateForm, call_type_id: Number(event.target.value) })} className="select-shell" required>
                  <option value={0}>Select call type</option>
                  {callTypes.map((callType) => <option key={callType.id} value={callType.id}>{callType.name}</option>)}
                </select>
              </div>
              <div>
                <label className="label-caps mb-1.5 block">Service Level</label>
                <select value={rateForm.service_level_id} onChange={(event) => setRateForm({ ...rateForm, service_level_id: Number(event.target.value) })} className="select-shell" required>
                  <option value={0}>Select service level</option>
                  {serviceLevels.filter((level) => level.is_active || level.id === rateForm.service_level_id).map((level) => (
                    <option key={level.id} value={level.id}>{serviceLevelLabel(level)}</option>
                  ))}
                </select>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="label-caps mb-1.5 block">Call Cost</label>
                  <input type="number" min="0" step="0.01" value={rateForm.rate} onChange={(event) => setRateForm({ ...rateForm, rate: Number(event.target.value) })} className="input-shell" required />
                </div>
                <label className="flex min-h-[46px] items-center gap-3 self-end rounded-md border border-slate-200 px-4 py-3 text-sm font-medium text-slate-700">
                  <input type="checkbox" checked={rateForm.is_active} onChange={(event) => setRateForm({ ...rateForm, is_active: event.target.checked })} />
                  Active rate
                </label>
              </div>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setShowRateForm(false)} className="button-ghost" disabled={saving}>Cancel</button>
                <button type="submit" className="button-primary" disabled={saving || !rateForm.call_type_id || !rateForm.service_level_id}>{saving ? "Saving..." : "Save Call Rate"}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
